// src/pages/Dashboard/DashboardComponents/SparklineDashboard.jsx
// Production D3 sparkline — smooth curves, responsive width, unique gradient per instance.
//
// ─── DATA NORMALIZER ─────────────────────────────────────────────────────────
// Uses the IDENTICAL unwrapping + price-field logic as StockChart.jsx so that
// whatever shape renders the full chart will also render the sparkline.
//
// Nesting unwrap (mirrors StockChart useMemo):
//   data.data.data[]  ← three-level Redux nesting
//   data.data[]       ← two-level
//   data[]            ← direct array
//
// Price field priority (mirrors StockChart canvas draw):
//   period_close ?? avg_close ?? close ?? c ?? price
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useId,
} from 'react';
import * as d3 from 'd3';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const DEFAULT_HEIGHT = 40;
const PADDING_Y = 2;

// ---------------------------------------------------------------------------
// normaliseSparklineData
// ---------------------------------------------------------------------------
// Converts whatever the Redux store hands us into a clean number[].
// Called once per render inside a useMemo — zero side-effects except the
// one-time DEV warning when the shape is completely unrecognised.

// ---------------------------------------------------------------------------
// extractPriceFromPoint
// ---------------------------------------------------------------------------
// Mirrors StockChart.jsx line 124-133 exactly.
// Priority: period_close → avg_close → close → c → price → 0

function extractPriceFromPoint(d) {
  const price =
    d.period_close ??
    d.avg_close    ??
    d.close        ??
    d.c            ??
    d.price        ??
    0;
  return Number(price);
}

// ---------------------------------------------------------------------------
// unwrapToArray
// ---------------------------------------------------------------------------
// Mirrors StockChart.jsx useMemo lines 31-50 exactly.
// Handles the THREE-level Redux nesting: data.data.data[]

function unwrapToArray(raw) {
  if (raw == null) return [];

  // Direct array
  if (Array.isArray(raw)) return raw;

  // Object with .data
  if (raw.data && typeof raw.data === 'object') {
    // Three-level: data.data.data[]
    if (raw.data.data && Array.isArray(raw.data.data)) return raw.data.data;
    // Two-level: data.data[]
    if (Array.isArray(raw.data)) return raw.data;
  }

  return [];
}

// ---------------------------------------------------------------------------
// normaliseSparklineData
// ---------------------------------------------------------------------------
// Step 1: unwrap nesting (same as StockChart useMemo)
// Step 2: extract prices (same as StockChart canvas draw)
// Step 3: filter out non-finite values

const _warnedShapes = new Set();

function normaliseSparklineData(raw) {
  if (raw == null) return [];

  // Handle plain number[] passed directly
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'number') {
    return raw.filter((v) => Number.isFinite(v));
  }

  const rows = unwrapToArray(raw);

  if (rows.length === 0) {
    if (process.env.NODE_ENV !== 'production' && raw != null) {
      const shapeKey = Array.isArray(raw)
        ? `array[${typeof raw[0]}]`
        : JSON.stringify(Object.keys(raw).sort());
      if (!_warnedShapes.has(shapeKey)) {
        _warnedShapes.add(shapeKey);
        console.warn(
          '[Sparkline] Could not unwrap data to an array.\n' +
          'Shape:', shapeKey, '\nValue:', raw,
          '\nExpected: data[], data.data[], or data.data.data[] — same shapes StockChart handles.'
        );
      }
    }
    return [];
  }

  // If the rows are plain numbers, use them directly
  if (typeof rows[0] === 'number') {
    return rows.filter((v) => Number.isFinite(v));
  }

  // Extract prices using the same field priority as StockChart
  const prices = rows
    .map(extractPriceFromPoint)
    .filter((v) => !isNaN(v) && isFinite(v) && v !== 0);

  if (prices.length === 0 && process.env.NODE_ENV !== 'production') {
    const shapeKey = JSON.stringify(Object.keys(rows[0] ?? {}).sort());
    if (!_warnedShapes.has(shapeKey)) {
      _warnedShapes.add(shapeKey);
      console.warn(
        '[Sparkline] Unwrapped rows but all prices were 0/NaN.\n' +
        'Keys on first row:', Object.keys(rows[0] ?? {}),
        '\nExpected one of: period_close, avg_close, close, c, price'
      );
    }
  }

  return prices;
}

// ---------------------------------------------------------------------------
// Sparkline
// ---------------------------------------------------------------------------

/**
 * Props:
 *   data           {any}       — see normaliseSparklineData above for accepted shapes
 *   height         {number}    — px height of the SVG (default 40)
 *   width          {number}    — optional fixed width; otherwise fills container
 *   positiveColor  {string}    — stroke/fill color when last > first
 *   negativeColor  {string}    — stroke/fill color when last < first
 *   neutralColor   {string}    — stroke/fill color when flat
 *   className      {string}    — extra class on the wrapper div
 *   strokeWidth    {number}    — line thickness (default 1.5)
 *   smooth         {bool}      — use CatmullRom curve (default true)
 *   showDot        {bool}      — show a dot at the last data point (default true)
 *   debugLabel     {string}    — optional ticker label for DEV console output
 */
const Sparkline = ({
  data,
  height = DEFAULT_HEIGHT,
  width: fixedWidth,
  positiveColor = '#22c55e',
  negativeColor = '#ef4444',
  neutralColor  = '#a1a1aa',
  className = '',
  strokeWidth = 1.5,
  smooth = true,
  showDot = true,
  debugLabel = '',
}) => {
  const containerRef = useRef(null);
  const svgRef       = useRef(null);
  const [containerWidth, setContainerWidth] = useState(fixedWidth || 0);

  // ── Unique gradient ID per component instance ─────────────────────────────
  const reactId    = useId();
  const uid        = useMemo(() => `sl-${reactId.replace(/:/g, '')}`, [reactId]);
  const gradientId = `${uid}-grad`;
  const clipId     = `${uid}-clip`;

  // ── Measure container width (sync, no flash) ──────────────────────────────
  useLayoutEffect(() => {
    if (fixedWidth) {
      setContainerWidth(fixedWidth);
      return;
    }
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 2) setContainerWidth(Math.floor(w));
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fixedWidth]);

  // ── Normalise data (handles ALL backend shapes) ───────────────────────────
  const safeData = useMemo(() => {
    const result = normaliseSparklineData(data);

    // DEV-only shape tracing — helps during wiring
    if (process.env.NODE_ENV !== 'production' && debugLabel) {
      if (result.length === 0 && data != null) {
        console.warn(
          `[Sparkline:${debugLabel}] Data resolved to empty array.\n` +
          'Raw value received:', data
        );
      } else if (result.length > 0) {
        console.debug(
          `[Sparkline:${debugLabel}] ✓ ${result.length} points. ` +
          `first=${result[0].toFixed(2)}, last=${result[result.length - 1].toFixed(2)}`
        );
      }
    }

    return result;
  }, [data, debugLabel]);

  // ── Derived values ────────────────────────────────────────────────────────
  const W = containerWidth;
  const H = height;

  const strokeColor = useMemo(() => {
    if (safeData.length < 2) return neutralColor;
    const last  = safeData[safeData.length - 1];
    const first = safeData[0];
    if (last > first) return positiveColor;
    if (last < first) return negativeColor;
    return neutralColor;
  }, [safeData, positiveColor, negativeColor, neutralColor]);

  // ── D3 render ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    if (!safeData.length || W === 0) return;

    const n = safeData.length;

    const xScale = d3
      .scaleLinear()
      .domain([0, n - 1])
      .range([0, W]);

    const [minVal, maxVal] = d3.extent(safeData);
    const range = maxVal - minVal || 1;

    const yScale = d3
      .scaleLinear()
      .domain([minVal - range * 0.05, maxVal + range * 0.05])
      .range([H - PADDING_Y, PADDING_Y]);

    // Defs — gradient + clipPath
    const defs = svg.append('defs');

    defs
      .append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '0%').attr('y2', '100%')
      .selectAll('stop')
      .data([
        { offset: '0%',   opacity: 0.28 },
        { offset: '100%', opacity: 0.03 },
      ])
      .join('stop')
      .attr('offset',       (d) => d.offset)
      .attr('stop-color',   strokeColor)
      .attr('stop-opacity', (d) => d.opacity);

    defs
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('width', W)
      .attr('height', H);

    // Area fill
    const areaGen = d3
      .area()
      .x((_, i) => xScale(i))
      .y0(H)
      .y1((d) => yScale(d))
      .curve(smooth ? d3.curveCatmullRom.alpha(0.5) : d3.curveLinear);

    svg
      .append('path')
      .datum(safeData)
      .attr('d', areaGen)
      .attr('fill', `url(#${gradientId})`)
      .attr('clip-path', `url(#${clipId})`);

    // Line
    const lineGen = d3
      .line()
      .x((_, i) => xScale(i))
      .y((d) => yScale(d))
      .curve(smooth ? d3.curveCatmullRom.alpha(0.5) : d3.curveLinear);

    svg
      .append('path')
      .datum(safeData)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', strokeColor)
      .attr('stroke-width', strokeWidth)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .attr('clip-path', `url(#${clipId})`);

    // End dot
    if (showDot && n >= 1) {
      svg
        .append('circle')
        .attr('cx', xScale(n - 1))
        .attr('cy', yScale(safeData[n - 1]))
        .attr('r', strokeWidth + 1)
        .attr('fill', strokeColor);
    }
  }, [safeData, W, H, strokeColor, strokeWidth, smooth, showDot, gradientId, clipId]);

  // ── Skeleton while measuring or data absent ───────────────────────────────
  if (!safeData.length || W === 0) {
    return (
      <div
        ref={containerRef}
        style={{
          width:  fixedWidth ? `${fixedWidth}px` : '100%',
          height: `${H}px`,
        }}
        className={`bg-slate-800/30 rounded animate-pulse ${className}`}
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width:  fixedWidth ? `${fixedWidth}px` : '100%',
        height: `${H}px`,
      }}
      className={className}
    >
      <svg
        ref={svgRef}
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-hidden="true"
        style={{ display: 'block', overflow: 'visible' }}
      />
    </div>
  );
};

export default Sparkline;